/**
 * AI badge ("KI") for AI-generated images and videos.
 *
 * Adds a small clickable badge to every image or flagged media element whose
 * alt text or file name contains an AI marker. Clicking the badge opens the
 * shared <dialog> rendered by snippets/ai-badge-modal.liquid.
 *
 * Configuration comes from a JSON blob rendered in the same snippet
 * (<script type="application/json" data-ai-badge-config>).
 *
 * The marker rule below MUST stay identical to the one in
 * snippets/ai-badge-flag.liquid.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Marker rule (keep in sync with snippets/ai-badge-flag.liquid)       */
  /* ------------------------------------------------------------------ */

  // Alt text: "#AI", "#KI", "[AI]" or "[KI]" as a separate word, any case.
  // Word boundaries are: start/end of text, whitespace and , . ; : ! ? ( )
  var ALT_MARKER = /(?:^|[\s,.;:!?()])(?:#ai|#ki|\[ai\]|\[ki\])(?=$|[\s,.;:!?()])/i;

  // File name: "_AI" or "_KI" followed by the end of the name, a dot,
  // an underscore or a hyphen (so "sofa_AI.jpg", "sofa_AI_1000x.jpg",
  // "sofa_ai-2.jpg" match, "sofa_air.jpg" does not).
  var FILE_MARKER = /_(?:ai|ki)(?=$|[._-])/i;

  function altMatches(alt) {
    return !!alt && ALT_MARKER.test(alt);
  }

  function fileNameFromUrl(url) {
    if (!url) return '';
    var clean = String(url).split('#')[0].split('?')[0];
    var parts = clean.split('/');
    return parts[parts.length - 1] || '';
  }

  function fileMatches(url) {
    var name = fileNameFromUrl(url);
    return !!name && FILE_MARKER.test(name);
  }

  /* ------------------------------------------------------------------ */
  /* Setup                                                               */
  /* ------------------------------------------------------------------ */

  var configEl = document.querySelector('[data-ai-badge-config]');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent || '{}');
  } catch (e) {
    return;
  }
  if (!config || !config.enabled) return;

  var SOURCE = config.source || 'both'; // 'alt' | 'filename' | 'both'
  var USE_ALT = SOURCE !== 'filename';
  var USE_FILE = SOURCE !== 'alt';
  var MIN_WIDTH = Number(config.minWidth) || 0;
  var CORNER = config.corner || 'top-left';
  var STRIP_ALT = !!config.stripAlt;

  var BADGE_CLASS = 'ai-badge';

  // Wonder controls that may sit in the same corner as the badge. When one
  // of them overlaps the badge, the badge moves below (top corners) or
  // above (bottom corners) the control. A <video controls> reserves its
  // native control bar at the bottom.
  var CONTROL_SELECTOR = [
    '.shoppable-video__control-button',
    '.wt-hero-video__sound-toggle',
    '.wt-video__overlay__controls',
    '.wt-product__save',
    '.wt-product__image-popup-opener',
    '.swiper-pagination',
    '.swiper-scrollbar',
    '.card__quick-add-container--desktop',
    '.shoppable-video__products',
    'video[controls]'
  ].join(',');
  var CONTROL_SCOPE = '[data-gallery], .shoppable-video__container, .background-video, .hero, .card__picture-container';
  var NATIVE_CONTROLS_HEIGHT = 56;
  var CONTROL_GAP = 6;
  var FLAG_ATTR = 'data-ai-generated';
  var DONE_ATTR = 'data-ai-badge-done';

  // Elements that never get a badge (cart, search suggestions, product
  // gallery thumbnails, swatches and anything explicitly opted out).
  var IGNORE_SELECTOR = [
    '[data-ai-badge-ignore]',
    '.' + BADGE_CLASS,
    'cart-drawer',
    '.cart-drawer',
    '.wt-cart',
    '.cart-item',
    'cart-items',
    'cart-notification',
    '.cart-notification',
    '.cart-notification-product__image',
    'predictive-search',
    '.predictive-search',
    '.search-result-products',
    '.suggest-item',
    '[data-thumbs]',
    '.thumbs-list',
    '.color-swatcher',
    '.color-swatch',
    '[class*="swatch"]',
    '.ai-badge-dialog'
  ].join(',');

  /* ------------------------------------------------------------------ */
  /* Matching                                                            */
  /* ------------------------------------------------------------------ */

  function imageUrl(img) {
    return (
      img.currentSrc ||
      img.getAttribute('src') ||
      img.getAttribute('data-src') ||
      (img.getAttribute('srcset') || '').split(',')[0].trim().split(' ')[0] ||
      ''
    );
  }

  function imgMatchesRule(img) {
    if (USE_ALT && altMatches(img.getAttribute('alt'))) return true;
    if (USE_FILE && fileMatches(imageUrl(img))) return true;
    if (USE_FILE) {
      var srcset = img.getAttribute('srcset');
      if (srcset && fileMatches(srcset.split(',')[0].trim().split(' ')[0])) return true;
    }
    return false;
  }

  // PhotoSwipe copies the thumbnail <img> into the lightbox as .pswp__img
  // with src = the <a href>. Map it back to the gallery link so the alt
  // rule works even when the marker was stripped from the alt attribute.
  function lightboxSourceMatches(img) {
    var src = img.getAttribute('src');
    if (!src) return false;
    var links = document.querySelectorAll('.photoswipe a[href]');
    for (var i = 0; i < links.length; i++) {
      if (links[i].getAttribute('href') !== src && links[i].href !== src) continue;
      if (links[i].hasAttribute(FLAG_ATTR)) return true;
      var thumb = links[i].querySelector('img');
      if (thumb && (thumb.hasAttribute(FLAG_ATTR) || imgMatchesRule(thumb))) return true;
      return false;
    }
    return false;
  }

  function isIgnored(el) {
    return !!el.closest(IGNORE_SELECTOR);
  }

  /**
   * Returns true when the element should carry a badge.
   * Sets data-ai-generated on matched images so that later passes
   * (e.g. after the alt marker was stripped) still recognise them.
   */
  function sourceMatches(el) {
    if (el.hasAttribute(FLAG_ATTR)) return true;
    if (el.tagName !== 'IMG') return false;
    if (imgMatchesRule(el)) {
      el.setAttribute(FLAG_ATTR, 'true');
      return true;
    }
    if (el.classList.contains('pswp__img') && lightboxSourceMatches(el)) {
      el.setAttribute(FLAG_ATTR, 'true');
      return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ */
  /* Host detection                                                      */
  /* ------------------------------------------------------------------ */

  function rectsMatch(a, b) {
    var tol = 2;
    return (
      Math.abs(a.width - b.width) <= tol &&
      Math.abs(a.height - b.height) <= tol &&
      Math.abs(a.top - b.top) <= tol &&
      Math.abs(a.left - b.left) <= tol
    );
  }

  /**
   * Finds the element the badge is appended to.
   * Walks up a few ancestors and prefers the first one that is already
   * positioned and has the same box as the media element. Falls back to
   * the nearest block-level ancestor with matching size, and finally to
   * the direct parent with an explicit offset.
   */
  function findHost(source, rect) {
    // PhotoSwipe lightbox: badge lives in the slide item, not in the
    // zoom wrapper (which is scaled/translated while zooming).
    var pswpItem = source.closest('.pswp__item');
    if (pswpItem) return { el: pswpItem, offset: null, lightbox: true };

    // Climb while the ancestors keep the same box as the media element and
    // take the OUTERMOST positioned one. Overlays (banner text, link
    // layers) are siblings of inner wrappers, and inner wrappers are often
    // transformed by scroll animations, which would trap the badge below
    // those overlays.
    var el = source.parentElement;
    var positioned = null;
    var unpositioned = null;
    var depth = 0;
    while (el && el !== document.body && depth < 6) {
      var cs = window.getComputedStyle(el);
      var display = cs.display;
      var isInline = display === 'inline' || display === 'contents';
      if (!isInline) {
        var er = el.getBoundingClientRect();
        if (rectsMatch(er, rect)) {
          if (cs.position !== 'static') positioned = el;
          else if (!unpositioned) unpositioned = el;
        } else {
          break; // sizes stopped matching; stop climbing
        }
      }
      el = el.parentElement;
      depth++;
    }
    if (positioned) return { el: positioned, offset: null };
    if (unpositioned) return { el: unpositioned, offset: null };

    // Flagged containers (video wrappers etc.) host their own badge.
    if (source.tagName !== 'IMG' && source.tagName !== 'VIDEO' && source.tagName !== 'IFRAME') {
      return { el: source, offset: null };
    }

    // Fallback: direct (block) parent with computed offsets.
    var parent = source.parentElement;
    while (parent && parent !== document.body) {
      var d = window.getComputedStyle(parent).display;
      if (d !== 'inline' && d !== 'contents') break;
      parent = parent.parentElement;
    }
    if (!parent || parent === document.body) return null;
    return { el: parent, offset: computeOffset(parent, rect) };
  }

  function computeOffset(host, rect) {
    var hr = host.getBoundingClientRect();
    return {
      top: rect.top - hr.top,
      left: rect.left - hr.left,
      right: hr.right - rect.right,
      bottom: hr.bottom - rect.bottom
    };
  }

  /* ------------------------------------------------------------------ */
  /* Badge creation                                                      */
  /* ------------------------------------------------------------------ */

  function createBadge(source, hostInfo) {
    var badge = document.createElement('span');
    badge.className = BADGE_CLASS + ' ' + BADGE_CLASS + '--' + CORNER;
    badge.setAttribute('role', 'button');
    badge.setAttribute('tabindex', '0');
    badge.setAttribute('aria-haspopup', 'dialog');
    badge.setAttribute('aria-label', config.ariaLabel || 'AI generated content – more information');
    badge.setAttribute('title', config.heading || '');

    if (config.iconUrl) {
      var icon = document.createElement('img');
      icon.src = config.iconUrl;
      icon.alt = '';
      icon.setAttribute('aria-hidden', 'true');
      icon.className = BADGE_CLASS + '__icon';
      badge.appendChild(icon);
    } else {
      badge.textContent = config.label || 'KI';
    }

    if (source.classList && (source.classList.contains('card__img--hover') || source.classList.contains('card__video--hover'))) {
      badge.classList.add(BADGE_CLASS + '--hover');
    }
    if (hostInfo.lightbox) {
      badge.classList.add(BADGE_CLASS + '--lightbox');
    }
    if (hostInfo.offset) {
      applyOffset(badge, hostInfo.offset);
      badge.setAttribute('data-ai-badge-offset', '');
    }

    badge._aiSource = source;
    return badge;
  }

  function applyOffset(badge, offset) {
    badge.style.setProperty('--ai-badge-extra-top', offset.top + 'px');
    badge.style.setProperty('--ai-badge-extra-left', offset.left + 'px');
    badge.style.setProperty('--ai-badge-extra-right', offset.right + 'px');
    badge.style.setProperty('--ai-badge-extra-bottom', offset.bottom + 'px');
  }

  function stripMarker(img) {
    if (!STRIP_ALT || img.tagName !== 'IMG') return;
    var alt = img.getAttribute('alt');
    if (!alt || !ALT_MARKER.test(alt)) return;
    var cleaned = alt
      .replace(/(^|[\s,.;:!?()])(?:#ai|#ki|\[ai\]|\[ki\])(?=$|[\s,.;:!?()])/gi, '$1')
      .replace(/\s{2,}/g, ' ')
      .trim();
    img.setAttribute('alt', cleaned);
  }

  /* ------------------------------------------------------------------ */
  /* Processing                                                          */
  /* ------------------------------------------------------------------ */

  function processSource(source) {
    if (source.hasAttribute(DONE_ATTR)) return;
    if (!sourceMatches(source)) return;
    if (isIgnored(source)) return;

    // Flagged containers take precedence over images inside them.
    if (source.tagName === 'IMG') {
      var flaggedAncestor = source.parentElement && source.parentElement.closest('[' + FLAG_ATTR + ']');
      if (flaggedAncestor) return;
    }

    var rect = source.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return; // not laid out yet, retry later
    if (MIN_WIDTH && rect.width < MIN_WIDTH) return;

    var hostInfo = findHost(source, rect);
    if (!hostInfo) return;

    var host = hostInfo.el;
    if (hostInfo.lightbox) {
      // PhotoSwipe keeps a placeholder <img> next to the real one: one badge per slide.
      if (host.querySelector('.' + BADGE_CLASS)) {
        source.setAttribute(DONE_ATTR, '');
        return;
      }
    } else if (window.getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }

    var badge = createBadge(source, hostInfo);
    host.appendChild(badge);
    source.setAttribute(DONE_ATTR, '');
    stripMarker(source);
    if (!hostInfo.lightbox) avoidControls(badge, host);
  }

  function cleanupBadges() {
    var badges = document.querySelectorAll('.' + BADGE_CLASS);
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      var src = badge._aiSource;
      // Clones (e.g. Swiper loop slides) carry no source: drop them, the
      // cloned image is processed again on this pass.
      if (!src || !src.isConnected || !badge.parentElement || !badge.parentElement.contains(src)) {
        if (badge.parentElement) badge.parentElement.removeChild(badge);
        continue;
      }
      if (badge.hasAttribute('data-ai-badge-offset')) {
        applyOffset(badge, computeOffset(badge.parentElement, src.getBoundingClientRect()));
      }
      if (!badge.classList.contains(BADGE_CLASS + '--hover')) {
        badge.classList.toggle(BADGE_CLASS + '--hidden', !isSourceVisible(src));
      }
      if (!badge.classList.contains(BADGE_CLASS + '--lightbox')) {
        avoidControls(badge, badge.parentElement);
      }
    }
  }

  function avoidControls(badge, host) {
    badge.style.removeProperty('--ai-badge-avoid');
    var br = badge.getBoundingClientRect();
    if (!br.width || !br.height) return;
    var hr = host.getBoundingClientRect();
    var top = CORNER.indexOf('top') === 0;
    var scope = host.closest(CONTROL_SCOPE) || host;
    var controls = scope.querySelectorAll(CONTROL_SELECTOR);
    var avoid = 0;
    for (var i = 0; i < controls.length; i++) {
      var c = controls[i];
      if (c === badge || badge.contains(c)) continue;
      var cr = c.getBoundingClientRect();
      if (!cr.width || !cr.height) continue;
      if (c.tagName === 'VIDEO') {
        if (top) continue;
        cr = { left: cr.left, right: cr.right, top: cr.bottom - NATIVE_CONTROLS_HEIGHT, bottom: cr.bottom };
      }
      var overlapsX = cr.left < br.right + CONTROL_GAP && cr.right > br.left - CONTROL_GAP;
      var overlapsY = cr.top < br.bottom + CONTROL_GAP && cr.bottom > br.top - CONTROL_GAP;
      if (!overlapsX || !overlapsY) continue;
      var need = top
        ? (cr.bottom + CONTROL_GAP) - br.top
        : br.bottom - (cr.top - CONTROL_GAP);
      if (need > avoid) avoid = need;
    }
    // Never push the badge out of its host.
    var room = top ? hr.bottom - br.bottom : br.top - hr.top;
    if (avoid > room) avoid = Math.max(0, room);
    if (avoid > 0) badge.style.setProperty('--ai-badge-avoid', Math.round(avoid) + 'px');
  }

  // Images stacked on top of each other (e.g. image-with-scrolling-text)
  // are faded with opacity: the badge follows its own image.
  function isSourceVisible(src) {
    var cs = window.getComputedStyle(src);
    if (cs.visibility === 'hidden' || cs.display === 'none') return false;
    if (parseFloat(cs.opacity) < 0.05) return false;
    var r = src.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function scan() {
    cleanupBadges();

    // Reset "done" on images whose badge was removed (clone cleanup).
    var done = document.querySelectorAll('[' + DONE_ATTR + ']');
    for (var d = 0; d < done.length; d++) {
      if (!hasOwnBadge(done[d])) done[d].removeAttribute(DONE_ATTR);
    }

    var flagged = document.querySelectorAll('[' + FLAG_ATTR + ']:not([' + DONE_ATTR + '])');
    for (var i = 0; i < flagged.length; i++) processSource(flagged[i]);

    var imgs = document.querySelectorAll('img:not([' + DONE_ATTR + ']):not([' + FLAG_ATTR + '])');
    for (var j = 0; j < imgs.length; j++) processSource(imgs[j]);
  }

  function hasOwnBadge(source) {
    var badges = document.querySelectorAll('.' + BADGE_CLASS);
    for (var i = 0; i < badges.length; i++) {
      if (badges[i]._aiSource === source) return true;
    }
    return false;
  }

  // setTimeout rather than requestAnimationFrame: rAF is paused in
  // background tabs, which would leave late DOM changes unprocessed.
  var scheduled = false;
  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      scan();
    }, 40);
  }

  /* ------------------------------------------------------------------ */
  /* Dialog                                                              */
  /* ------------------------------------------------------------------ */

  var dialog = document.getElementById('AiBadgeDialog');
  var lastTrigger = null;

  function openDialog(trigger) {
    if (!dialog) return;
    lastTrigger = trigger;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    var closeBtn = dialog.querySelector('[data-ai-badge-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeDialog() {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  if (dialog) {
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDialog(); // backdrop click
      if (event.target.closest('[data-ai-badge-close]')) closeDialog();
    });
    dialog.addEventListener('close', function () {
      var trigger = lastTrigger;
      lastTrigger = null;
      if (!trigger) return;
      window.setTimeout(function () {
        if (trigger.isConnected) {
          try { trigger.focus(); } catch (e) { /* noop */ }
        }
      }, 0);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Events (delegated, so Swiper clones work without re-binding)        */
  /* ------------------------------------------------------------------ */

  function badgeFromEvent(event) {
    var target = event.target;
    if (!target || !target.closest) return null;
    return target.closest('.' + BADGE_CLASS);
  }

  document.addEventListener(
    'click',
    function (event) {
      var badge = badgeFromEvent(event);
      if (!badge) return;
      event.preventDefault();
      event.stopPropagation();
      openDialog(badge);
    },
    true
  );

  // Stop the pointer events from reaching Swiper / PhotoSwipe handlers.
  ['pointerdown', 'mousedown', 'touchstart', 'touchend', 'pointerup', 'mouseup'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        if (badgeFromEvent(event)) event.stopPropagation();
      },
      true
    );
  });

  document.addEventListener(
    'keydown',
    function (event) {
      var badge = badgeFromEvent(event);
      if (!badge) return;
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        event.stopPropagation();
        openDialog(badge);
      }
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* Observers                                                           */
  /* ------------------------------------------------------------------ */

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
        scheduleScan();
        return;
      }
      if (m.type === 'attributes') {
        if (m.attributeName === 'style') {
          var t = m.target;
          if (!(t.tagName === 'IMG' || t.tagName === 'VIDEO' || t.hasAttribute(FLAG_ATTR))) continue;
        }
        scheduleScan();
        return;
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'alt', 'class', 'style']
  });

  document.addEventListener('transitionend', function (event) {
    if (event.propertyName === 'opacity' || event.propertyName === 'visibility') scheduleScan();
  }, true);

  window.addEventListener('resize', scheduleScan);
  window.addEventListener('load', scheduleScan);
  document.addEventListener('shopify:section:load', scheduleScan);
  document.addEventListener('shopify:section:reorder', scheduleScan);
  // Images that load later may change their box (no width/height attrs).
  document.addEventListener('load', function (event) {
    if (event.target && event.target.tagName === 'IMG') scheduleScan();
  }, true);

  scan();
})();
